import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Typography, Button, CircularProgress, InputBase, IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import NotesIcon from "@mui/icons-material/Notes";
import GroupsIcon from "@mui/icons-material/Groups";
import VideocamIcon from "@mui/icons-material/Videocam";
import moment from "moment";
import { list_sessions_api } from "../../../DAL/SchoolPortal/Tutors";
import SessionDetail from "./SessionDetail";
import {
  border, textPrimary, textSecondary, textMuted, accent, font,
  warning, secondaryButtonSx, subjectAccentColor,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import { prettyName } from "../StudentRecord/recordTheme";
import { useSchool } from "../../../Hooks/SchoolContext";
import { regionOf } from "../../../utils/region";

/**
 * SESSIONS — what is coming, then what has been.
 *
 * WHY THE PAGE IS SPLIT AND NOT FILTERED
 * This was one table with chips: All, Upcoming, Debrief owed, Recorded,
 * Cancelled. Two of those five were furniture. Upcoming rows were already
 * pinned to the top of the table, so "Upcoming" only ever removed the history,
 * and "All" only ever put it back — a control whose entire effect is to undo
 * the other control. Worse, they shared one pagination: page two of the
 * history scrolled next Tuesday off the screen, so the half of this page an
 * instructor opens daily was reachable only from page one.
 *
 * So the split is structural. COMING UP is short, complete, never paged and
 * never filtered by an archaeology chip — every session you have not taught
 * yet, soonest first, always at the top of the page. HISTORY is unbounded,
 * searched, filtered and paged, because it is a different job done at a
 * different time.
 *
 * THE CHIPS THAT SURVIVED ARE THE ONES THAT DISCRIMINATE. "Debrief owed"
 * answers "what do I still owe" — a question the sort order cannot answer.
 * "Cancelled" is out of the default view for the reason the diary hides them
 * and reachable because "did we ever run that November series?" is exactly
 * what someone opens a ledger to ask.
 *
 * ONE SEARCH, BOTH HALVES. A name is a name: typing "Priya" to find last
 * month's lesson and typing it to check next Tuesday are the same gesture, and
 * a box that searched only the archive would answer the second one wrong.
 *
 * PAGING AND SEARCH ARE THE SERVER'S. Filtering a page in the browser searches
 * the page, not the history, and gets quietly emptier the longer somebody
 * teaches — worst for the people with most to look back on. Every filter here
 * is a query parameter and the count under the table is the real total.
 */

const H = { fontFamily: font };
const PER_PAGE = 25;

/**
 * Everything not yet taught, in one screen.
 *
 * Not paged on purpose: an instructor with more than fifty future sessions
 * booked has a diary problem, not a pagination problem, and the count says so
 * rather than the list quietly stopping.
 */
const UPCOMING_LIMIT = 50;

const HISTORY_FILTERS = [
  { key: "", label: "Everything" },
  { key: "debrief", label: "Debrief owed" },
  { key: "recording", label: "Recorded" },
  { key: "cancelled", label: "Cancelled" },
];

const KINDS = [
  { key: "", label: "All" },
  { key: "one_to_one", label: "1:1" },
  { key: "class", label: "Classes" },
];

const money = (n, c) =>
  c ? new Intl.NumberFormat(undefined, { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n || 0) : null;

/** One formatter for the portal — see recordTheme. "Principles of Flight". */
const prettySubject = (s) => (s ? prettyName(s) : null);

const cell = { ...H, fontSize: "12.5px", color: textPrimary, minWidth: 0 };
const headCell = {
  ...H, fontSize: "10px", fontWeight: 800, letterSpacing: "0.07em",
  textTransform: "uppercase", color: textMuted, whiteSpace: "nowrap",
};

/**
 * One template per table, used by its header and its rows, so nothing drifts.
 * The two tables differ by one column because the two halves answer different
 * questions: before a session the useful fact is WHERE, after it is what the
 * session still owes.
 */
/**
 * The status column was 92px and its contents are `noWrap`, so the longest
 * thing it ever says — "Awaiting the student", the one row that is actually
 * work — was clipped mid-word. Every column here is now sized for its longest
 * real value rather than its average one.
 */
const COLS = {
  upcoming: { xs: "104px minmax(0,1fr) 64px", md: "128px minmax(0,1.15fr) minmax(0,1fr) 62px 140px 84px" },
  past: { xs: "88px minmax(0,1fr) 64px", md: "104px minmax(0,1.15fr) minmax(0,1fr) 62px 148px 84px" },
};

/**
 * "Today 14:00" beats "31 Aug 25" for something that has not happened.
 *
 * A future date is read as a countdown — is it today, is it this week — and a
 * bare numeric date makes the reader do that arithmetic. A past date is read
 * as a coordinate, so it keeps the numbers.
 */
function whenLabel(iso) {
  const m = moment(iso);
  const day = m.isSame(moment(), "day") ? "Today"
    : m.isSame(moment().add(1, "day"), "day") ? "Tomorrow"
      : m.isSame(moment(), "week") ? m.format("ddd")
        : m.format("D MMM");
  return { day, time: m.format("HH:mm") };
}

function SectionHead({ title, note, right }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1, flexWrap: "wrap" }}>
      <Typography sx={{ ...H, fontSize: "14px", fontWeight: 800, color: textPrimary }}>{title}</Typography>
      {note && <Typography sx={{ ...H, fontSize: "12px", color: textSecondary }}>{note}</Typography>}
      <Box sx={{ flex: 1 }} />
      {right}
    </Box>
  );
}

/** A time proposed and not yet answered. */
const PENDING = ["pending_instructor_approval", "pending_student_confirmation"];

/** One row, both tables. The mode decides the last two columns, nothing else. */
function Row({ r, mode, onOpen, showInstructor, region }) {
  const isClass = r.kind === "class";
  const w = whenLabel(r.startedAt);
  const today = mode === "upcoming" && moment(r.startedAt).isSame(moment(), "day");
  const pending = PENDING.includes(r.status);

  /**
   * COLOUR IS FOR WHAT IS COMING, NOT FOR WHAT IS DONE.
   *
   * A subject's colour is a wayfinding device: it tells you at a glance which
   * of Friday's four sessions is the weather one. That is only useful while
   * you still have to turn up to it. On history the same colour says nothing —
   * every row is equally finished — and fifteen coloured bars down a list you
   * are reading chronologically is decoration competing with the dates.
   *
   * So: subject colour on `upcoming`, nothing on `past`. Today still outranks
   * the subject, because "in the next few hours" beats "about weather".
   */
  const subjectColour = mode === "upcoming" ? subjectAccentColor(r.subject, region) : null;
  const edge = today ? accent : subjectColour;

  return (
    <Box
      onClick={() => onOpen(r)}
      sx={{
        display: "grid", gridTemplateColumns: COLS[mode], gap: 2, alignItems: "center",
        px: 2, py: 1.5, cursor: "pointer",
        // Today is the only row on this page somebody has to act on in the next
        // few hours, and it should be findable from across the room.
        backgroundColor: today ? `${accent}0f` : "transparent",
        borderLeft: `3px solid ${edge || "transparent"}`,
        borderBottom: `1px solid ${border}`, "&:last-of-type": { borderBottom: "none" },
        "&:hover": { backgroundColor: today ? `${accent}18` : "#FAF8F5" },
      }}
    >
      {mode === "upcoming" ? (
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6, minWidth: 0 }}>
          <Typography sx={{ ...cell, fontWeight: 800, color: today ? accent : textPrimary, whiteSpace: "nowrap" }}>
            {w.day}
          </Typography>
          <Typography sx={{ ...cell, color: textSecondary, fontVariantNumeric: "tabular-nums" }}>{w.time}</Typography>
        </Box>
      ) : (
        <Typography sx={{ ...cell, color: textSecondary, fontVariantNumeric: "tabular-nums" }}>
          {moment(r.startedAt).format("D MMM YY")}
        </Typography>
      )}

      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0 }}>
          {isClass && <GroupsIcon sx={{ fontSize: 15, color: textMuted, flexShrink: 0 }} />}
          <Typography noWrap sx={{ ...cell, fontWeight: 700 }}>
            {/* A class has no one student; naming it "Unknown student" would be
                a bug report wearing a row's clothes. */}
            {isClass ? (r.title || "Group class") : r.student}
          </Typography>
        </Box>
        {/* WHOSE LESSON IT IS, once the list stops being only yours. On a
            school-wide list every row without a name is a row a manager has to
            open to identify — which is the whole job of the column. */}
        {showInstructor && r.instructor && (
          <Typography noWrap sx={{ ...H, fontSize: "11px", color: textMuted, mt: 0.1 }}>
            {r.instructor}
          </Typography>
        )}
      </Box>

      <Typography noWrap sx={{ ...cell, color: textSecondary, display: { xs: "none", md: "block" } }}>
        {isClass
          ? `${r.seats} of ${r.capacity} booked`
          : [prettySubject(r.subject), r.lesson ? `L${r.lesson}` : null].filter(Boolean).join(" · ") || "—"}
      </Typography>

      <Typography sx={{ ...cell, color: textSecondary, fontVariantNumeric: "tabular-nums", display: { xs: "none", md: "block" } }}>
        {r.minutes}m
      </Typography>

      <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.6, minWidth: 0 }}>
        {mode === "upcoming" ? (
          pending ? (
            // The newest thing in the week and the only upcoming row that is
            // work rather than a commitment. It says WHO it is waiting on,
            // because "pending" alone does not tell you whether to chase.
            <Typography noWrap sx={{ ...H, fontSize: "11px", fontWeight: 700, color: warning }}>
              {r.status === "pending_student_confirmation" ? "Awaiting the student" : "Awaiting you"}
            </Typography>
          ) : (
          <>
            <VideocamIcon sx={{ fontSize: 15, color: textMuted, flexShrink: 0 }} titleAccess="Online" />
            <Typography noWrap sx={{ ...H, fontSize: "11px", color: textSecondary }}>
              {moment(r.startedAt).fromNow()}
            </Typography>
          </>
          )
        ) : r.status === "cancelled" ? (
          <Typography noWrap sx={{ ...H, fontSize: "11px", fontWeight: 700, color: textMuted }}>Cancelled</Typography>
        ) : (
          <>
            {/* What this session still OWES comes first and in colour; what it
                merely HAS is an icon. A debrief is work, a recording is not. */}
            {r.needsDebrief && (
              <Typography noWrap sx={{ ...H, fontSize: "11px", fontWeight: 700, color: warning }}>Debrief owed</Typography>
            )}
            {r.openTodos > 0 && (
              <Typography noWrap sx={{ ...H, fontSize: "11px", fontWeight: 700, color: warning }}>
                {r.openTodos} to do
              </Typography>
            )}
            {r.recap === "ready" && (
              <NotesIcon sx={{ fontSize: 15, color: accent, flexShrink: 0 }} titleAccess="Summarised" />
            )}
            {r.hasRecording && (
              <PlayCircleOutlineIcon sx={{ fontSize: 16, color: accent, flexShrink: 0 }} titleAccess="Recorded" />
            )}
          </>
        )}
      </Box>

      <Typography sx={{ ...cell, fontWeight: 700, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        {money(r.amount, r.currency) || "—"}
      </Typography>
    </Box>
  );
}

function Table({ mode, rows, loading, empty, onOpen, showInstructor }) {
  // Read here rather than threaded from the page: the region only decides
  // which tree a subject key resolves against, and both tables want the same
  // answer. `subjectAccentColor` already falls back safely when it is absent.
  const { school } = useSchool();
  const region = regionOf(school);
  const headings = mode === "upcoming"
    ? ["When", "Student / class", "Subject / seats", "Length", "", "Value"]
    : ["Date", "Student / class", "Subject / seats", "Length", "", "Paid"];

  return (
    <Box sx={{ border: `1px solid ${border}`, borderRadius: "10px", overflow: "hidden", backgroundColor: "#fff" }}>
      <Box sx={{
        display: "grid", gridTemplateColumns: COLS[mode], gap: 2,
        px: 2, py: 1, borderBottom: `1px solid ${border}`, backgroundColor: "#FAF8F5",
      }}>
        {headings.map((h, i) => (
          <Typography
            key={i}
            sx={{
              ...headCell,
              textAlign: i === headings.length - 1 ? "right" : "left",
              display: i >= 2 && i <= 4 ? { xs: "none", md: "block" } : "block",
            }}
          >
            {h}
          </Typography>
        ))}
      </Box>
      {loading ? (
        <Box sx={{ py: 4.5, textAlign: "center" }}><CircularProgress size={20} sx={{ color: accent }} /></Box>
      ) : !rows.length ? (
        <Box sx={{ py: 4, px: 2, textAlign: "center" }}>{empty}</Box>
      ) : rows.map((r) => (
        <Row key={`${r.kind}-${r.id}`} r={r} mode={mode} onOpen={onOpen} showInstructor={showInstructor} region={region} />
      ))}
    </Box>
  );
}

export default function Sessions({ onDebrief, onPrep, onOpenClass, onOpen, canSeeSchool = false }) {
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  /**
   * Whose sessions. Defaults to the viewer's own even for an owner, because
   * "what am I teaching on Thursday" is the daily question and "what did the
   * school deliver" is the monthly one — and a manager who also teaches would
   * otherwise have to filter their own week out of forty rows every morning.
   */
  const [who, setWho] = useState("me");

  const [up, setUp] = useState({ rows: [], total: 0, loading: true, error: null });
  const [past, setPast] = useState({ rows: [], total: 0, page: 1, pages: 1, limit: PER_PAGE, loading: true, error: null });
  const [has, setHas] = useState("");
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(1);

  /**
   * Debounced, and the timer is cleared on unmount. A search that fires per
   * keystroke sends eight requests for "Marcus" and renders whichever lands
   * last, which is not necessarily the last one sent.
   */
  const timer = useRef(null);
  useEffect(() => {
    timer.current = setTimeout(() => { setQ(term); setPage(1); }, 300);
    return () => clearTimeout(timer.current);
  }, [term]);

  const loadUpcoming = useCallback(async () => {
    setUp((p) => ({ ...p, loading: true }));
    const r = await list_sessions_api({ scope: "upcoming", limit: UPCOMING_LIMIT, q, kind, who });
    if (r?.code === 200) setUp({ rows: r.sessions || [], total: r.total || 0, loading: false, error: null });
    else setUp({ rows: [], total: 0, loading: false, error: r?.message || "Could not load what is coming up" });
  }, [q, kind, who]);

  const loadPast = useCallback(async () => {
    setPast((p) => ({ ...p, loading: true }));
    const r = await list_sessions_api({ scope: has === "cancelled" ? "" : "past", page, limit: PER_PAGE, q, has, kind, who });
    if (r?.code === 200) {
      setPast({
        rows: r.sessions || [], total: r.total || 0, page: r.page, pages: r.pages, limit: r.limit,
        loading: false, error: null,
      });
    } else {
      setPast((p) => ({ ...p, rows: [], total: 0, loading: false, error: r?.message || "Could not load your history" }));
    }
  }, [page, q, has, kind, who]);

  useEffect(() => { loadUpcoming(); }, [loadUpcoming]);
  useEffect(() => { loadPast(); }, [loadPast]);

  /**
   * A class row and a 1:1 row open different things, and that is not a
   * inconsistency to hide: the session drawer answers "what happened", and a
   * class that has not happened yet is opened to change it — seats, price,
   * calling it off — which is the management block's job, not a ledger's.
   */
  const open = (r) => {
    // `onOpen` wins when the parent supplies one: a session has its own
    // address now (/school/sessions/:id) and a table embedded in a page can
    // navigate to it, while the same table used somewhere with no router
    // still falls back to the drawer.
    if (onOpen) { onOpen(r); return; }
    if (r.kind === "class" && r.upcoming && onOpenClass) { onOpenClass(r); return; }
    setOpenId(r.id);
  };

  const filtering = Boolean(q || has || kind);
  const from = past.total === 0 ? 0 : (past.page - 1) * past.limit + 1;
  const to = Math.min(past.page * past.limit, past.total);

  const owed = useMemo(() => past.rows.filter((r) => r.needsDebrief).length, [past.rows]);

  return (
    <Box>
      {/* ONE SEARCH FOR THE WHOLE PAGE. A name is a name — looking for last
          month's lesson and checking next Tuesday are the same gesture. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Box sx={{
          display: "flex", alignItems: "center", gap: 0.8, px: 1.2, py: 0.6,
          border: `1px solid ${border}`, borderRadius: "9px", backgroundColor: "#fff",
          flex: 1, minWidth: 220, maxWidth: 360,
        }}>
          <SearchIcon sx={{ fontSize: 17, color: textMuted }} />
          <InputBase
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search every session — students, subjects, notes…"
            sx={{ ...H, fontSize: "12.5px", flex: 1 }}
          />
          {term && (
            <IconButton size="small" onClick={() => setTerm("")} sx={{ p: 0.2 }}>
              <CloseIcon sx={{ fontSize: 14, color: textMuted }} />
            </IconButton>
          )}
        </Box>
        {canSeeSchool && (
          <Box sx={{ display: "flex", gap: 0.3, backgroundColor: "rgba(0,0,0,0.04)", borderRadius: "999px", p: 0.3 }}>
            {[["me", "Mine"], ["school", "Whole school"]].map(([k, lbl]) => (
              <Box
                key={k}
                onClick={() => { setWho(k); setPage(1); }}
                sx={{
                  px: 1.1, py: 0.35, borderRadius: "999px", cursor: "pointer", whiteSpace: "nowrap",
                  ...H, fontSize: "11.5px", fontWeight: 700,
                  color: who === k ? textPrimary : textSecondary,
                  backgroundColor: who === k ? "#fff" : "transparent",
                }}
              >
                {lbl}
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 0.3, backgroundColor: "rgba(0,0,0,0.04)", borderRadius: "999px", p: 0.3 }}>
          {KINDS.map((k) => (
            <Box
              key={k.key || "any"}
              onClick={() => { setKind(k.key); setPage(1); }}
              sx={{
                px: 1.1, py: 0.35, borderRadius: "999px", cursor: "pointer", whiteSpace: "nowrap",
                ...H, fontSize: "11.5px", fontWeight: 700,
                color: kind === k.key ? textPrimary : textSecondary,
                backgroundColor: kind === k.key ? "#fff" : "transparent",
              }}
            >
              {k.label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── COMING UP ─────────────────────────────────────────────────────── */}
      <SectionHead
        title="Coming up"
        note={up.loading ? null
          : up.total === 0 ? null
            : `${up.total} session${up.total === 1 ? "" : "s"}${up.total > up.rows.length ? ` · showing the next ${up.rows.length}` : ""}`}
      />
      {up.error && <Typography sx={{ ...H, fontSize: "12.5px", color: warning, mb: 1 }}>{up.error}</Typography>}
      <Table
        mode="upcoming"
        rows={up.rows}
        loading={up.loading}
        onOpen={open}
        showInstructor={who === "school"}
        empty={
          <>
            <Typography sx={{ ...H, fontSize: "13px", fontWeight: 600, color: textPrimary }}>
              {filtering ? "Nothing coming up matches that" : "Nothing booked yet"}
            </Typography>
            <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, mt: 0.4 }}>
              {filtering
                ? "Your history below may still have it."
                : "Confirmed lessons and classes appear here until you have taught them."}
            </Typography>
          </>
        }
      />

      {/* ── HISTORY ───────────────────────────────────────────────────────── */}
      <Box sx={{ mt: 3.5 }}>
        <SectionHead
          title="History"
          note={past.loading ? null : `${past.total} session${past.total === 1 ? "" : "s"}${owed ? ` · ${owed} on this page still owe a debrief` : ""}`}
          right={
            <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
              {HISTORY_FILTERS.map((f) => (
                <Box
                  key={f.key || "all"}
                  onClick={() => { setHas(f.key); setPage(1); }}
                  sx={{
                    px: 1.2, py: 0.45, borderRadius: "999px", cursor: "pointer", whiteSpace: "nowrap",
                    ...H, fontSize: "11.5px", fontWeight: 700,
                    border: `1px solid ${has === f.key ? accent : border}`,
                    color: has === f.key ? "#fff" : textSecondary,
                    backgroundColor: has === f.key ? accent : "#fff",
                  }}
                >
                  {f.label}
                </Box>
              ))}
            </Box>
          }
        />
        {past.error && <Typography sx={{ ...H, fontSize: "12.5px", color: warning, mb: 1 }}>{past.error}</Typography>}
        <Table
          mode="past"
          rows={past.rows}
          loading={past.loading}
          onOpen={open}
          showInstructor={who === "school"}
          empty={
            <>
              {/* An empty SEARCH and an empty HISTORY are different facts and
                  must not share a sentence — one means "try another word", the
                  other means "you have not taught yet". */}
              <Typography sx={{ ...H, fontSize: "13px", fontWeight: 600, color: textPrimary }}>
                {filtering ? "Nothing matches that" : "Nothing taught yet"}
              </Typography>
              <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, mt: 0.4 }}>
                {filtering
                  ? "Try a different name, subject or filter."
                  : "Once a session has run it moves down here, with its recording, transcript and recap."}
              </Typography>
            </>
          }
        />

        {/* The range AND the total, because "Next" with no denominator gives no
            idea whether there are two more or two hundred. */}
        {past.total > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.25, flexWrap: "wrap" }}>
            <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, fontVariantNumeric: "tabular-nums" }}>
              {from}–{to} of {past.total}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              disabled={past.page <= 1 || past.loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              startIcon={<ChevronLeftIcon sx={{ fontSize: 16 }} />}
              sx={{ ...secondaryButtonSx, minHeight: 0, py: 0.5, px: 1.2, fontSize: "12px" }}
            >
              Previous
            </Button>
            <Button
              disabled={past.page >= past.pages || past.loading}
              onClick={() => setPage((p) => p + 1)}
              endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
              sx={{ ...secondaryButtonSx, minHeight: 0, py: 0.5, px: 1.2, fontSize: "12px" }}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>

      <SessionDetail
        id={openId}
        onClose={() => setOpenId(null)}
        onPrep={(session) => {
          setOpenId(null);
          const r = [...up.rows, ...past.rows].find((x) => String(x.id) === String(session.id));
          if (r) onPrep?.(r);
        }}
        onDebrief={(sessionId) => {
          setOpenId(null);
          const r = [...up.rows, ...past.rows].find((x) => String(x.id) === String(sessionId));
          if (r) onDebrief?.(r);
        }}
      />
    </Box>
  );
}
